import { View, StyleSheet } from 'react-native';
import { SettingSection } from '@/components/settings/SettingSection';
import { SettingItem } from '@/components/settings/SettingItem';
import { BorderRadius } from '@/constants/spacing';

interface AuthInfoSectionProps {
  signInMethod: 'google' | 'email';
  providerName: string | null;
  providerEmail: string | null;
  accountCreatedAt: string | null;
  textColor: string;
  backgroundColor: string;
}

/**
 * AuthInfoSection Component
 * Displays read-only authentication information from the user's account.
 * Shows sign-in method, provider metadata, and account creation date.
 */
export function AuthInfoSection({
  signInMethod,
  providerName,
  providerEmail,
  accountCreatedAt,
  textColor,
  backgroundColor,
}: AuthInfoSectionProps) {
  // Format sign-in method for display
  const signInMethodLabel = signInMethod === 'google' ? 'Google' : 'Email & Password';

  // Format account creation date
  const formatCreationDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <SettingSection title="ACCOUNT INFORMATION" textColor={textColor}>
      <View style={[styles.container, { backgroundColor }]}>
        <SettingItem
          icon="log-in-outline"
          label="Sign-in Method"
          value={signInMethodLabel}
          textColor={textColor}
          isFirst
          showChevron={false}
        />

        {providerName && (
          <SettingItem
            icon="person-outline"
            label="Name from Provider"
            value={providerName}
            textColor={textColor}
            showChevron={false}
          />
        )}

        {providerEmail && (
          <SettingItem
            icon="mail-outline"
            label="Email Address"
            value={providerEmail}
            textColor={textColor}
            showChevron={false}
          />
        )}

        <SettingItem
          icon="calendar-outline"
          label="Account Created"
          value={formatCreationDate(accountCreatedAt)}
          textColor={textColor}
          isLast
          showChevron={false}
        />
      </View>
    </SettingSection>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BorderRadius.md,
    overflow: 'hidden',
  },
});
